/**
 * Apartsales — TabBar.
 *
 * Apple iOS 26+ canonical bottom tab bar. 5 tabs, icon-only (no labels).
 *
 * Tab order (per spec):
 *   1. Главная   — AppStore Today-style discovery + top deals
 *   2. Лента     — infinite vertical feed of properties (browse mode)
 *   3. Поиск     — central; magnifier+sparkle = AI-powered search
 *   4. Карта     — geographic discovery
 *   5. Паспорт   — user avatar
 *
 * Visual contract (мировой стандарт — Instagram / Linear / Stripe / iOS 14+):
 *   - Форма иконки всегда одна — outline.
 *   - Меняется ТОЛЬКО цвет: inactive = label (чёрный/белый), active = brand-blue.
 *   - НИКАКИХ filled-вариантов в tab bar — они дают «синий блоб» и сжирают
 *     внутренний глиф (sparkle лупы, окно дома и т.д.).
 *   - Фон ячейки всегда прозрачный — никаких pill-подложек под активной иконкой.
 *
 * Контраст «чёрный ⇄ синий» — высоковесомый, иконки не «уходят в фон».
 *   - Color transitions smoothly (180ms) on tab change.
 *   - Press feedback: scale 0.92 (lighter than 0.90 — feels more refined).
 *   - Photo tab (Passport): brand-blue ring when active, hairline when not.
 *
 * Same color logic applies to both light and dark themes — brand-blue is
 * universally readable on bg, and label-2 token is theme-aware.
 *
 * Solid bg with top hairline divider, fixed 48px height, 5 equal cells.
 */

import type { ReactElement } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  AISearchIcon,
  FeedIcon,
  HomeIcon,
  MapPinIcon,
} from './icons';

const USER_PHOTO = '/billionsx-avatar.jpeg';

interface IconTab {
  kind: 'icon';
  id: string;
  label: string;
  to: string;
  Icon: (props: { size?: number; filled?: boolean }) => ReactElement;
}

interface PhotoTab {
  kind: 'photo';
  id: string;
  label: string;
  to: string;
  photo: string;
}

type Tab = IconTab | PhotoTab;

const TABS: Tab[] = [
  { kind: 'icon', id: 'home', label: 'Главная', to: '/', Icon: HomeIcon },
  { kind: 'icon', id: 'feed', label: 'Лента', to: '/feed', Icon: FeedIcon },
  { kind: 'icon', id: 'search', label: 'Поиск', to: '/search', Icon: AISearchIcon },
  { kind: 'icon', id: 'map', label: 'Карта', to: '/map', Icon: MapPinIcon },
  { kind: 'photo', id: 'passport', label: 'Паспорт', to: '/passport', photo: USER_PHOTO },
];

export function TabBar() {
  const location = useLocation();

  return (
    <nav
      aria-label="Главная навигация"
      className="relative z-40 shrink-0 border-t bg-bg"
      style={{
        borderTopColor: 'var(--color-hairline)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <ul className="flex h-12 items-stretch">
        {TABS.map((tab) => {
          const active =
            tab.to === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(tab.to);

          return (
            <li key={tab.id} className="flex-1">
              <Link
                to={tab.to}
                aria-label={tab.label}
                aria-current={active ? 'page' : undefined}
                className="relative flex h-full items-center justify-center outline-none transition-[color,transform] duration-[180ms] ease-out active:scale-[0.92]"
                style={{
                  color: active
                    ? 'var(--color-brand-blue)'
                    : 'var(--color-label)',
                }}
              >
                {tab.kind === 'icon' ? (
                  <tab.Icon size={26} filled={false} />
                ) : (
                  <img
                    src={tab.photo}
                    alt=""
                    width={26}
                    height={26}
                    loading="lazy"
                    decoding="async"
                    className="h-[26px] w-[26px] rounded-full object-cover transition-[box-shadow] duration-[180ms] ease-out"
                    style={{
                      boxShadow: active
                        ? '0 0 0 2px var(--color-brand-blue)'
                        : '0 0 0 1px var(--color-hairline)',
                    }}
                  />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
